/**
 * Software Update Policy wizard — mode-first state shape.
 *
 * Three modes (per Smplify Create Patching Policy spec):
 *   - "pinned"          → com.apple.configuration.softwareupdate.enforcement.specific
 *   - "rolling"         → same kind, but generated server-side (resolver TBD)
 *   - "update_behavior" → com.apple.configuration.softwareupdate.settings
 *
 * One target platform per policy (matches payload kind semantics). Form
 * fields render mode-aware and platform-aware so the wizard *feels* like
 * creating a patching policy, not a generic config profile.
 */

export type SuMode = "pinned" | "rolling" | "update_behavior";

export type SuPlatform = "macos" | "ios" | "ipados" | "tvos" | "visionos";

export const SU_PLATFORM_META: Record<
  SuPlatform,
  { label: string; supportsDdm: boolean; supportsRsr: boolean }
> = {
  macos: { label: "macOS", supportsDdm: true, supportsRsr: true },
  ios: { label: "iOS", supportsDdm: true, supportsRsr: true },
  ipados: { label: "iPadOS", supportsDdm: true, supportsRsr: true },
  tvos: { label: "tvOS", supportsDdm: false, supportsRsr: false },
  visionos: { label: "visionOS", supportsDdm: false, supportsRsr: false },
};

/** OS floor for `enforcement.specific` (Pinned / Rolling). */
export const PINNED_OS_FLOOR: Record<SuPlatform, string | null> = {
  ios: "17.0",
  macos: "14.0",
  ipados: "17.0",
  tvos: "18.4",
  visionos: "26.0",
};

/** OS floor for `softwareupdate.settings` (Update Behavior). */
export const SETTINGS_OS_FLOOR: Record<SuPlatform, string | null> = {
  ios: "18.0",
  macos: "15.0",
  ipados: "18.0",
  tvos: "18.4",
  visionos: "26.0",
};

export const SU_KIND_PINNED = "com.apple.configuration.softwareupdate.enforcement.specific";
export const SU_KIND_SETTINGS = "com.apple.configuration.softwareupdate.settings";

// ───── Mode A — Pinned ─────────────────────────────────────────────────────

export interface PinnedConfigState {
  /** Required. `^\d+(\.\d+){0,2}$` — UI-enforced (server accepts any string). */
  targetVersion: string;
  /** Optional, e.g. "24F74" or "20A242a" (supplemental letter). */
  targetBuild: string;
  /** Required. Device local time, format `YYYY-MM-DDTHH:MM:SS` (no Z/offset/ms). */
  deadline: string;
  /** Optional org page URL. HTTPS only. */
  detailsUrl: string;
}

// ───── Mode B — Rolling (interim, server resolver not built) ──────────────

export type RollingLane = "latest" | "n-1" | "specific-major";

export interface RollingConfigState {
  lane: RollingLane;
  /** When lane = "specific-major", which major to track (e.g. "15"). */
  major: string;
  /** Days to wait after release before applying. */
  delayDays: number;
  /** Local time to apply at, "HH:mm". */
  applyTime: string;
  includeMinor: boolean;
  includeMajor: boolean;
  /** Snapshot version to write if user opts into "Save as Pinned snapshot". */
  snapshotVersion: string;
}

// ───── Mode C — Update Behavior ───────────────────────────────────────────

export type SuAuto = "Allowed" | "AlwaysOn" | "AlwaysOff";
export type SuRecommendedCadence = "All" | "Oldest" | "Newest";

export interface BetaProgramRef {
  description: string;
  token: string;
}

export interface UpdateBehaviorState {
  // Deferrals (1–90 integers; UI uses 0 to mean "unset")
  deferMajorDays: number; // macOS only
  deferMinorDays: number; // macOS only (on iOS this also defers major)
  deferSystemDays: number; // macOS only
  deferCombinedDays: number; // iOS / iPadOS / tvOS / visionOS

  recommendedCadence: SuRecommendedCadence | ""; // iOS family

  autoDownload: SuAuto;
  autoInstallOs: SuAuto;
  autoInstallSecurity: SuAuto; // macOS only (singular key!)

  rsrEnable: boolean;
  rsrEnableRollback: boolean;

  allowStandardUserOsUpdates: boolean; // macOS only
  notifications: boolean;

  // Beta (AppleSeed) — Offer and Require are mutually exclusive.
  betaEnrollment: SuAuto;
  betaOfferPrograms: BetaProgramRef[];
  betaRequireProgram: BetaProgramRef | null;
}

// ───── Wizard root state ──────────────────────────────────────────────────

export interface SoftwareUpdateWizardState {
  mode: SuMode;
  platform: SuPlatform;
  name: string;
  description: string;

  pinned: PinnedConfigState;
  rolling: RollingConfigState;
  behavior: UpdateBehaviorState;

  /** Tag ids picked in Scope step. */
  tagIds: string[];
}

// ───── Defaults ───────────────────────────────────────────────────────────

export const defaultPinned = (): PinnedConfigState => ({
  targetVersion: "",
  targetBuild: "",
  deadline: "",
  detailsUrl: "",
});

export const defaultRolling = (): RollingConfigState => ({
  lane: "latest",
  major: "",
  delayDays: 7,
  applyTime: "20:00",
  includeMinor: true,
  includeMajor: false,
  snapshotVersion: "",
});

export const defaultBehavior = (): UpdateBehaviorState => ({
  deferMajorDays: 0,
  deferMinorDays: 0,
  deferSystemDays: 0,
  deferCombinedDays: 0,
  recommendedCadence: "",
  autoDownload: "Allowed",
  autoInstallOs: "Allowed",
  autoInstallSecurity: "Allowed",
  rsrEnable: true,
  rsrEnableRollback: true,
  allowStandardUserOsUpdates: true,
  notifications: true,
  betaEnrollment: "Allowed",
  betaOfferPrograms: [],
  betaRequireProgram: null,
});

export const defaultWizardState = (): SoftwareUpdateWizardState => ({
  mode: "pinned",
  platform: "macos",
  name: "",
  description: "",
  pinned: defaultPinned(),
  rolling: defaultRolling(),
  behavior: defaultBehavior(),
  tagIds: [],
});

export const MODE_META: Record<SuMode, { label: string; kind: string | null }> = {
  pinned: { label: "Pinned version", kind: SU_KIND_PINNED },
  rolling: { label: "Rolling (latest approved)", kind: SU_KIND_PINNED },
  update_behavior: { label: "Update behavior", kind: SU_KIND_SETTINGS },
};
